import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { forwardRef, ReactNode, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ensureYoutubeApiReady } from '../../stores/playerListStore';
import { useMobxInstance } from '../../utils/mobxUtil';

export class YoutubePlayerStore {
    videoId: string | null = null;
    isInitialized = false;
    el: HTMLElement | null = null;
    player: YT.Player | null = null;
    /**
     * 플레이어 상태
     * -1은 실제 YT.PlayerState.UNSTARTED 값이지만
     * 스크립트가 로드되지 않았을 수 있으므로 직접 설정
     */
    playerState: number = -1;
    currentTime = 0;
    duration = 0;
    timerId: number | undefined = undefined;

    // 구간 반복 및 제어
    repeatStart: number | null = null;
    repeatEnd: number | null = null;
    isRepeating = false;
    pauseAtEnd = false; // 구간 끝에서 멈출지 여부

    constructor(videoId: string | null) {
        makeObservable(this, {
            player: observable,
            playerState: observable,
            currentTime: observable,
            duration: observable,
            repeatStart: observable,
            repeatEnd: observable,
            isRepeating: observable,
            pauseAtEnd: observable,
            isPlaying: computed,
            setPlayer: action,
            setPlayerState: action,
            updateCurrentTime: action,
            clear: action,
        });

        this.videoId = videoId;
    }

    async initialize(el: HTMLElement): Promise<void> {
        if (typeof window === 'undefined' || !el) {
            return;
        }

        if (this.el === el && this.isInitialized) {
            return;
        }

        await ensureYoutubeApiReady();

        this.clear();
        this.el = el;

        const updateCurrentTimePeriodically = () => {
            clearTimeout(this.timerId);
            runInAction(() => {
                this.updateCurrentTime();

                if (this.isPlaying) {
                    this.timerId = window.setTimeout(updateCurrentTimePeriodically, 100);
                }
            });
        };

        const options: YT.PlayerOptions = {
            width: '100%',
            height: '100%',
            videoId: this.videoId || undefined,
            playerVars: {
                playsinline: 1,
                // 자막이 나와야 한다면 영어로
                cc_lang_pref: 'en',
                // 자막 표시는 사용자 설정대로
                cc_load_policy: 0,
            },
            events: {
                onReady: () => {
                    this.setPlayer(instance);
                },
                onStateChange: (e: YT.OnStateChangeEvent) => {
                    runInAction(() => {
                        this.setPlayerState(e.data);

                        // 1: YT.PlayerState.PLAYING
                        if (e.data === 1) {
                            updateCurrentTimePeriodically();

                            // 기본적으로 자막을 꺼줌
                            // @ts-ignore 타입스크립트에서 지원하지 않아서 타입은 무시해둠
                            instance.unloadModule('captions');
                        } else {
                            clearTimeout(this.timerId);
                            // 재생 중이 아닐 때도 seek 등으로 위치가 변경되면 currentTime 업데이트
                            this.updateCurrentTime();
                        }
                    });
                },
                onError: (e: YT.OnErrorEvent) => {
                    if (import.meta.env.DEV) {
                        console.log('ERROR:', e);
                    }
                },
            },
        };

        const instance: YT.Player = new window.YT.Player(el, options);

        this.isInitialized = true;
    }

    get isPlaying(): boolean {
        // 1: YT.PlayerState.PLAYING
        return !!this.player && this.playerState === 1;
    }

    play(): void {
        this.player?.playVideo();
    }

    pause(): void {
        this.player?.pauseVideo();
    }

    setPlayer(player: YT.Player): void {
        this.player = player;

        // duration 설정
        try {
            const duration = player.getDuration();
            if (duration) {
                this.duration = duration;
            }
        } catch (e) {
            // player가 아직 준비되지 않았을 수 있음
        }
    }

    setPlayerState(playerState: number): void {
        /*
            -1 –시작되지 않음
            0 – 종료
            1 – 재생 중
            2 – 일시중지
            3 – 버퍼링
            5 – 동영상 신호
        */
        this.playerState = playerState;
    }

    /**
     * 특정 시간으로 이동
     */
    seekTo(time: number): void {
        this.player?.seekTo(time, true);
    }

    /**
     * 특정 시간으로 이동 후 재생
     */
    seekToAndPlay(time: number): void {
        this.seekTo(time);
        this.play();
    }

    /**
     * 구간 반복 설정
     */
    setRepeatRange(start: number, end: number): void {
        this.repeatStart = start;
        this.repeatEnd = end;
        this.isRepeating = true;
    }

    /**
     * 구간 반복 해제
     */
    clearRepeatRange(): void {
        this.repeatStart = null;
        this.repeatEnd = null;
        this.isRepeating = false;
    }

    /**
     * 구간 끝에서 멈출지 설정
     */
    setPauseAtEnd(pause: boolean): void {
        this.pauseAtEnd = pause;
    }

    /**
     * 현재 재생 시간 및 duration 업데이트
     */
    updateCurrentTime(): void {
        if (!this.player) {
            return;
        }

        try {
            const currentTime = this.player.getCurrentTime();
            const duration = this.player.getDuration();

            this.currentTime = currentTime;
            if (duration && this.duration !== duration) {
                this.duration = duration;
            }

            // 구간 반복 또는 멈춤 처리
            if (this.isRepeating && this.repeatEnd !== null) {
                if (currentTime >= this.repeatEnd) {
                    if (this.pauseAtEnd) {
                        this.pause();
                        // 멈춘 후에는 반복 상태를 해제하여 다시 재생했을 때 바로 다시 멈추지 않게 함
                        this.clearRepeatRange();
                    } else if (this.repeatStart !== null) {
                        // 반복 구간 시작으로 이동
                        this.seekTo(this.repeatStart);
                    }
                }
            }
        } catch (e) {
            // player가 아직 준비되지 않았을 수 있음
        }
    }

    clear(): void {
        clearTimeout(this.timerId);
        this.timerId = undefined;
        this.player?.destroy();
        this.player = null;
        this.el = null;
    }
}

export interface YoutubePlayerRef {
    seekTo: (time: number) => void;
    seekToAndPlay: (time: number) => void;
    setRepeatRange: (start: number, end: number) => void;
    clearRepeatRange: () => void;
    setPauseAtEnd: (pause: boolean) => void;
    play: () => void;
    pause: () => void;
    getCurrentTime: () => number;
    getDuration: () => number;
}

const YoutubePlayer = observer(
    forwardRef<
        YoutubePlayerRef,
        {
            videoId: string;
            store?: YoutubePlayerStore;
            onTimeUpdate?: (currentTime: number, duration: number) => void;
            onPlayerStateChange?: (state: number) => void;
        }
    >(({ videoId, store, onTimeUpdate, onPlayerStateChange }, ref): React.ReactNode => {
        const elRef = useRef<HTMLDivElement>(null);
        const [isMaskClicked, setMaskClicked] = useState(false);
        const internalStore = useMobxInstance(YoutubePlayerStore, videoId);
        const viewStore = store || internalStore;

        // ref API 노출
        useImperativeHandle(ref, () => ({
            seekTo: (time: number) => viewStore.seekTo(time),
            seekToAndPlay: (time: number) => viewStore.seekToAndPlay(time),
            setRepeatRange: (start: number, end: number) => viewStore.setRepeatRange(start, end),
            clearRepeatRange: () => viewStore.clearRepeatRange(),
            setPauseAtEnd: (pause: boolean) => viewStore.setPauseAtEnd(pause),
            play: () => viewStore.play(),
            pause: () => viewStore.pause(),
            getCurrentTime: () => viewStore.currentTime,
            getDuration: () => viewStore.duration,
        }));

        useEffect(() => {
            const el = elRef.current;

            if (viewStore && el) {
                viewStore.initialize(el);
            }

            return () => {
                if (viewStore) {
                    viewStore.clear();
                }
            };
        }, [viewStore]);

        // onTimeUpdate 콜백 호출 (컴포넌트에서 처리)
        useEffect(() => {
            if (onTimeUpdate) {
                onTimeUpdate(viewStore.currentTime, viewStore.duration);
            }
        }, [viewStore.currentTime, viewStore.duration, onTimeUpdate]);

        // playerState 변경 시 콜백 호출
        useEffect(() => {
            if (onPlayerStateChange) {
                onPlayerStateChange(viewStore.playerState);
            }
        }, [viewStore.playerState, onPlayerStateChange]);

        // iOS 에서는 터치 스타트 때 유튜브가 바로 재생되어 버리는 이슈가 있어, 명시적으로 클릭했을 때만 재생하도록 한다.
        // 한 번 재생한 후에 사용자가 트랙을 이동하는 등의 별도의 컨트롤을 할 수 있으므로 다시 마스크를 올리진 않는다.
        let mask: ReactNode;
        if (!isMaskClicked && !viewStore.isPlaying) {
            mask = (
                <div
                    role="button"
                    className="absolute top-0 left-0 h-full w-full cursor-pointer"
                    onClick={(e) => {
                        e.preventDefault();
                        setMaskClicked(true);
                        viewStore.play();
                    }}
                ></div>
            );
        }

        return (
            <div className="relative leading-none">
                {/* 임베딩한 유튜브 사이즈 최적화
            @see https://community.canvaslms.com/t5/Canvas-Instructional-Designer/Embedding-a-YouTube-Player-with-Responsive-Aspect-Ratio/ba-p/273447 */}
                <div className="relative h-0 overflow-hidden pb-[56.25%]">
                    <div ref={elRef} className="absolute top-0 left-0 h-full w-full"></div>
                    {mask}
                </div>
            </div>
        );
    }),
);

export default YoutubePlayer;
